import { useEffect, useState } from 'react';
import type { TenantSettings } from '@orqforge/shared';
import { apiClient } from '../lib/api-client';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Input } from '../components/ui/Input';

const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Paris',
  'Asia/Tokyo',
  'Asia/Shanghai',
];

const DATE_FORMATS = ['YYYY-MM-DD', 'MM/DD/YYYY', 'DD/MM/YYYY'];

const selectClass =
  'h-9 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

function errText(err: unknown, fallback: string): string {
  if (!(err instanceof Error) || !err.message) return fallback;
  try {
    const parsed = JSON.parse(err.message) as { message?: unknown };
    if (typeof parsed.message === 'string' && parsed.message) return parsed.message;
    if (Array.isArray(parsed.message)) return parsed.message.map(String).join(', ');
  } catch {
    return err.message;
  }
  return err.message;
}

const empty: TenantSettings = {
  id: '',
  tenantId: '',
  timezone: 'UTC',
  dateFormat: 'YYYY-MM-DD',
};

export function SettingsPage() {
  const [settings, setSettings] = useState<TenantSettings>(empty);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void apiClient.getSettings().then(setSettings).catch((err) => setError(errText(err, 'Failed to load settings')));
  }, []);

  async function saveSettings() {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const next = await apiClient.updateSettings({
        timezone: settings.timezone,
        dateFormat: settings.dateFormat,
        cursorApiKeyRef: settings.cursorApiKeyRef?.trim() || null,
      });
      setSettings(next);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(errText(err, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Settings</h1>
      </div>
      <Card className="mb-6">
        <CardContent className="grid gap-4 pt-6">
          <h2 className="text-base font-semibold">General</h2>
          {error ? (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
              {error}
            </div>
          ) : null}
          <div className="grid max-w-md gap-3">
            <label className="grid gap-1 text-sm">
              <span className="text-xs text-gray-500">Timezone</span>
              <select
                className={selectClass}
                value={settings.timezone}
                onChange={(e) => setSettings((s) => ({ ...s, timezone: e.target.value }))}
              >
                {TIMEZONES.map((zone) => (
                  <option key={zone} value={zone}>
                    {zone}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-1 text-sm">
              <span className="text-xs text-gray-500">Date format</span>
              <select
                className={selectClass}
                value={settings.dateFormat}
                onChange={(e) => setSettings((s) => ({ ...s, dateFormat: e.target.value }))}
              >
                {DATE_FORMATS.map((format) => (
                  <option key={format} value={format}>
                    {format}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-1 text-sm">
              <span className="text-xs text-gray-500">Cursor API key (Vault ref, optional)</span>
              <Input
                placeholder="vault:secret/tenants/acme#cursorApiKey"
                value={settings.cursorApiKeyRef ?? ''}
                onChange={(e) => setSettings((s) => ({ ...s, cursorApiKeyRef: e.target.value }))}
              />
              <span className="text-xs text-gray-500">
                Overrides <code>CURSOR_API_KEY</code> for Cursor Cloud Agents in this tenant. Leave empty to use the env key.
              </span>
            </label>
          </div>
          <div>
            <Button size="sm" disabled={saving} onClick={() => void saveSettings()}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
            {saved ? <span className="ml-2 text-xs text-green-600">Saved</span> : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
